import { makeId } from '@db/makeId';
import { FILE_TYPE } from '@ent/entity-type';
import {
  isAllowedFileContentType,
  isImageContentType,
  maxBytesForContentType
} from './constants';
import { blobPathnameForFile } from './blob-url';
import { withDetectedContentType } from './content-signature';
import type { FileAttachmentArgs } from '../zero/mutator-types';
import type { FileParentType } from './constants';
import { normalizeFilename } from './blob-url';
import { getClientFileStorage } from './storage/client';

export type UploadTargetContext =
  | {
      organizationId: string;
      parentType: 'chat_messages';
      channelId: string;
    }
  | {
      organizationId: string;
      parentType: Exclude<FileParentType, 'chat_messages'>;
      parentId: string;
    };

export type PendingLocalFile = FileAttachmentArgs & {
  previewUrl?: string;
  localFile?: File;
};

export type FileValidationCode =
  'unsupported-type' | 'too-large' | 'empty-file' | 'too-many-files';

export async function prepareLocalFiles(
  files: readonly File[],
  remaining: number
): Promise<{ files: PendingLocalFile[]; errors: FileValidationCode[] }> {
  const prepared: PendingLocalFile[] = [];
  const errors: FileValidationCode[] = [];
  if (files.length > remaining) {
    errors.push('too-many-files');
  }
  for (const file of files.slice(0, Math.max(remaining, 0))) {
    try {
      prepared.push(await prepareLocalFile(file));
    } catch (error) {
      const code = error instanceof Error ? error.message : '';
      errors.push(
        code === 'too-large' ||
          code === 'empty-file' ||
          code === 'unsupported-type'
          ? code
          : 'unsupported-type'
      );
    }
  }
  return { files: prepared, errors: [...new Set(errors)] };
}

async function imageDimensions(
  file: File
): Promise<{ width: number; height: number } | null> {
  if (
    !isImageContentType(file.type) ||
    typeof createImageBitmap !== 'function'
  ) {
    return null;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

export async function prepareLocalFile(input: File): Promise<PendingLocalFile> {
  // Trust the bytes over the extension-derived File.type; the server does.
  const file = await withDetectedContentType(input);
  if (!isAllowedFileContentType(file.type)) {
    throw new Error('unsupported-type');
  }
  if (file.size > maxBytesForContentType(file.type)) {
    throw new Error('too-large');
  }
  if (file.size <= 0) {
    throw new Error('empty-file');
  }
  const dimensions = await imageDimensions(file);
  const filename = normalizeFilename(file.name || 'file');
  return {
    id: makeId(FILE_TYPE),
    pathname: '',
    contentType: file.type,
    sizeBytes: file.size,
    filename,
    width: dimensions?.width ?? null,
    height: dimensions?.height ?? null,
    previewUrl: isImageContentType(file.type)
      ? URL.createObjectURL(file)
      : undefined,
    localFile: file
  };
}

export async function uploadPendingFile(
  pending: PendingLocalFile,
  target: UploadTargetContext,
  onProgress?: (percentage: number) => void
): Promise<FileAttachmentArgs> {
  if (!pending.localFile) {
    return fileAttachmentArgs(pending);
  }
  const pathname = blobPathnameForFile(
    target.organizationId,
    target.parentType,
    target.parentType === 'chat_messages' ? target.channelId : target.parentId,
    pending.id,
    pending.filename
  );
  const uploaded = await getClientFileStorage().uploadFile(
    pathname,
    pending.localFile,
    {
      contentType: pending.contentType,
      clientPayload: JSON.stringify({
        ...target,
        fileId: pending.id,
        filename: pending.filename,
        contentType: pending.contentType,
        sizeBytes: pending.sizeBytes
      }),
      onProgress
    }
  );
  if (uploaded.pathname !== pathname) {
    throw new Error('upload-path-mismatch');
  }
  return {
    id: pending.id,
    pathname: uploaded.pathname,
    contentType: pending.contentType,
    sizeBytes: pending.sizeBytes,
    filename: pending.filename,
    width: pending.width,
    height: pending.height
  };
}

function fileAttachmentArgs(pending: PendingLocalFile): FileAttachmentArgs {
  return {
    id: pending.id,
    pathname: pending.pathname,
    contentType: pending.contentType,
    sizeBytes: pending.sizeBytes,
    filename: pending.filename,
    width: pending.width,
    height: pending.height
  };
}

export function revokePreview(pending: PendingLocalFile) {
  if (pending.previewUrl) {
    URL.revokeObjectURL(pending.previewUrl);
  }
}
